import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tasksApi, teamsApi } from '../../services/api';
import { useProjectsQuery, useTeamsQuery, useCustomFieldsQuery } from '../../hooks/queries';
import { taskKeys } from '../../lib/queryKeys';
import TasksTable from '../../components/TasksTable';
import { PageHeaderSkeleton, TaskListSkeleton } from '../../components/skeletons/PageSkeletons';
import BulkActionBar from '../../components/BulkActionBar';
import ViewMenu from '../../components/ViewMenu';
import TasksHeader from '../../components/tasks/TasksHeader';
import { useViewTabs } from '../../hooks/useViewTabs';
import { usePageTaskFilters } from '../../hooks/usePageTaskFilters';
import TaskFilterBar from '../../components/tasks/TaskFilterBar';
import ListView from '../../components/ListView';
import KanbanBoard from '../../components/KanbanBoard';
import ProjectCalendar from '../../components/ProjectCalendar';
import TimelineView from '../../components/TimelineView';
import GalleryView from '../../components/GalleryView';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { useTask } from '../../contexts/TaskContext';
import { useMobileTitle } from '../../components/mobile/mobileTitle';
import { useAuth } from '../../contexts/AuthContext';
import { filterMineTasks } from '../../lib/taskFilters';
import { EMPTY_ARRAY } from '../../lib/empty';

const TasksPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team');

  const qc = useQueryClient();

  // Tasks: team-scoped or personal - keep both keys distinct so we can opt
  // into different query lifetimes if needed; the global MutationCache
  // invalidator covers both.
  const tasksQuery = useQuery({
    queryKey: teamId ? ['tasks', 'team', teamId] : taskKeys.list(),
    queryFn: () =>
      (teamId ? teamsApi.getTasks(teamId) : tasksApi.getAll()).then((r) => r.data),
  });
  const projectsQuery = useProjectsQuery();
  const teamsQuery = useTeamsQuery();
  const customFieldsQuery = useCustomFieldsQuery();

  const tasks = tasksQuery.data || EMPTY_ARRAY;
  const projects = projectsQuery.data || EMPTY_ARRAY;
  const teams = teamsQuery.data || EMPTY_ARRAY;
  const customFieldDefs = customFieldsQuery.data || EMPTY_ARRAY;
  const teamName = teamId ? (teams.find((t) => t.id === teamId)?.name || null) : null;
  // Tasks (with the Planner as its Calendar); a team's tasks (?team=) are a page of their own.
  const title = teamName ? t('tasks.team_tasks', { name: teamName }) : t('planner.tasks_label');
  useMobileTitle(teamName ? title : null);
  const loading = tasksQuery.isPending || projectsQuery.isPending;

  const refetchTasks = useCallback(
    () => qc.invalidateQueries({ queryKey: taskKeys.all }),
    [qc]
  );

  const { tabs, activeView, hiddenCatalog, isLocked, loading: viewsLoading, actions: viewActions } = useViewTabs({ kind: 'tasks' });
  const activeViewId = activeView?.id || null;
  const viewMode = activeView?.type || null;
  const { openTaskCreation } = useTask();

  // My Tasks: the tasks I'm responsible for (assigned to me or created by me), as
  // on Home. Teammates' tasks are in their projects and on a team's page (?team=).
  const { user } = useAuth();
  const visibleTasks = useMemo(
    () => (teamId ? tasks : filterMineTasks(tasks, user?.id)),
    [tasks, teamId, user]
  );

  // Shared filter/search across ALL view types (List, Board, Table, …). The
  // filter is owned here at the page level and applied to whichever view is
  // active, so every view can filter - not just Table. Persisted per view.
  const filterViewId = activeViewId ? `tasks-${activeViewId}` : 'tasks';
  // The table puts its grouping, columns and completed toggle into the filter bar's row.
  const [toolbarSlot, setToolbarSlot] = useState(null);
  // Only a new slot counts: while the tab is hidden (layouts/TabScreens.jsx) its refs are
  // let go, and taking that as "no slot" drew the whole page twice per tab switch.
  const keepToolbarSlot = useCallback((el) => { if (el) setToolbarSlot(el); }, []);
  const filters = usePageTaskFilters({ tasks: visibleTasks, viewId: filterViewId, projects, teamId });
  const shownTasks = filters.filteredTasks;

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionMetaRef = useRef({ customStatuses: [], projects: [] });
  const selectedTasksRef = useRef([]);

  const handleSelectionChange = useCallback((ids, selectedTasks, meta) => {
    setSelectedIds(new Set(ids));
    selectedTasksRef.current = selectedTasks || [];
    if (meta) selectionMetaRef.current = meta;
  }, []);

  const handleOptimisticUpdate = useCallback(
    (taskId, updates) => {
      const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
      lists.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        qc.setQueryData(
          key,
          list.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
        );
      });
    },
    [qc]
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton />
        <TaskListSkeleton rows={7} />
      </div>
    );
  }

  const openCount = visibleTasks.filter(t => t.status !== 'completed').length;

  return (
      <div className="space-y-3 md:space-y-4">
        {/* Title, List | Calendar (the Planner) and the one action. Phones add tasks
            with the tab bar's New. */}
        <TasksHeader
          mode="list"
          title={title}
          meta={`${openCount}/${visibleTasks.length}`}
          showSwitch={!teamId}
          action={(
            <Button
              data-tour="quick-add"
              onClick={() => openTaskCreation(teamId ? { teamId } : null)}
              size="sm"
              className="hidden md:inline-flex bg-amber-500 text-black hover:bg-amber-400 h-8 px-3 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              {t('tasks.add_task')}
            </Button>
          )}
        />

        {/* Search and filter for every view, with the saved views (per-user order and
            default) as one menu at the end of the row. */}
        <div className="flex items-start gap-1.5">
          <div className="flex-1 min-w-0">
            <TaskFilterBar {...filters} toolbarSlotRef={keepToolbarSlot} />
          </div>
          <ViewMenu
            data-tour="view-tabs"
            tabs={tabs}
            activeView={activeView}
            hiddenCatalog={hiddenCatalog}
            isLocked={isLocked}
            actions={viewActions}
            loading={viewsLoading}
            customFieldDefs={customFieldDefs}
            className="flex-shrink-0"
          />
        </div>

        {/* View content */}
        {viewMode === 'list' && (
          <ListView
            tasks={shownTasks}
            projects={projects}
            onUpdate={refetchTasks}
            visibleFields={activeView?.config?.visible_fields}
            customFieldDefs={customFieldDefs}
          />
        )}

        {viewMode === 'table' && (
          <TasksTable
            tasks={shownTasks}
            projects={projects}
            teams={teams}
            onUpdate={refetchTasks}
            onOptimisticUpdate={handleOptimisticUpdate}
            showProjectColumn={true}
            showSearch={false}
            showFilters={false}
            showGrouping={true}
            showAddButton={false}
            showCompletedToggle={true}
            defaultGroupBy="project"
            emptyMessage={t('tasks.no_tasks_found')}
            emptyDescription={t('tasks.no_tasks_found_description')}
            tableId={activeViewId ? `tasks-${activeViewId}` : 'tasks'}
            showBulkSelect={true}
            onSelectionChange={handleSelectionChange}
            toolbarTarget={toolbarSlot}
          />
        )}

        {viewMode === 'board' && (
          <KanbanBoard
            tasks={shownTasks}
            projects={projects}
            onUpdate={refetchTasks}
            visibleFields={activeView?.config?.visible_fields}
            customFieldDefs={customFieldDefs}
          />
        )}

        {viewMode === 'calendar' && (
          <ProjectCalendar
            tasks={shownTasks}
            projects={projects}
            onUpdate={refetchTasks}
            visibleFields={activeView?.config?.visible_fields}
            customFieldDefs={customFieldDefs}
          />
        )}

        {viewMode === 'timeline' && (
          <TimelineView
            tasks={shownTasks}
            projects={projects}
            onUpdate={refetchTasks}
            visibleFields={activeView?.config?.visible_fields}
            customFieldDefs={customFieldDefs}
          />
        )}

        {viewMode === 'gallery' && (
          <GalleryView
            tasks={shownTasks}
            projects={projects}
            onUpdate={refetchTasks}
            visibleFields={activeView?.config?.visible_fields}
            customFieldDefs={customFieldDefs}
          />
        )}

        {viewMode === 'table' && selectedIds.size > 0 && (
          <BulkActionBar
            selectedIds={selectedIds}
            selectedTasks={selectedTasksRef.current}
            meta={selectionMetaRef.current}
            projects={projects}
            onClear={() => setSelectedIds(new Set())}
            onUpdate={refetchTasks}
            onDelete={() => { refetchTasks(); setSelectedIds(new Set()); }}
          />
        )}

      </div>
  );
};

export default TasksPage;
